import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, User, ArrowRight, Eye } from 'lucide-react';

const EditRequestBanner = ({ editRequest, currentUser }) => {
  if (!editRequest) return null;

  const getStatusBadge = (status) => {
    const statusMap = {
      'Pending_EAC_Review': { label: 'Pending EAC Review', color: 'bg-yellow-100 text-yellow-800' },
      'Pending_SDO_Review': { label: 'Pending SDO Review', color: 'bg-orange-100 text-orange-800' },
      'Pending_ADC_Review': { label: 'Pending ADC Review', color: 'bg-blue-100 text-blue-800' },
      'Pending_DC_Approval': { label: 'Pending DC Approval', color: 'bg-purple-100 text-purple-800' }
    };
    const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeSummary = () => {
    const changes = [];

    if (editRequest.requestedChanges && Object.keys(editRequest.requestedChanges).length > 0) {
      const fieldCount = Object.keys(editRequest.requestedChanges).length;
      changes.push(`${fieldCount} field${fieldCount > 1 ? 's' : ''}`);
    }

    if (editRequest.documentChanges) {
      const docChanges = [];
      if (editRequest.documentChanges.landRegistryDocument) docChanges.push('Land Registry');
      if (editRequest.documentChanges.registryMapDocument) docChanges.push('Registry Map');
      if (editRequest.documentChanges.scannedDocuments?.length) {
        docChanges.push(`${editRequest.documentChanges.scannedDocuments.length} document(s)`);
      }
      if (docChanges.length > 0) {
        changes.push(docChanges.join(', '));
      }
    }

    return changes.length > 0 ? changes.join('; ') : 'No changes specified';
  };

  // Check if current user is the assigned reviewer
  const isAssignedReviewer = () => {
    if (!currentUser) return false;

    switch (editRequest.status) {
      case 'Pending_EAC_Review':
        return currentUser.role === 'eac' && editRequest.assignedEAC === currentUser.id;
      case 'Pending_SDO_Review':
        return currentUser.role === 'sdo' && editRequest.assignedSDO === currentUser.id;
      case 'Pending_ADC_Review':
        return currentUser.role === 'adc' && editRequest.assignedADC === currentUser.id;
      case 'Pending_DC_Approval':
        return currentUser.role === 'dc';
      default:
        return false;
    }
  };

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg p-4 mb-6 shadow-sm">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertTriangle className="h-6 w-6 text-yellow-500" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-semibold text-yellow-800 flex items-center gap-2">
            Pending Edit Request
            {getStatusBadge(editRequest.status)}
          </h3>

          <div className="mt-2 text-sm text-yellow-700 space-y-1">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>
                Initiated by <strong>{editRequest.initiator?.fullName || 'Unknown'}</strong>
                {editRequest.initiator?.role && (
                  <span className="text-yellow-600 ml-1">({editRequest.initiator.role.toUpperCase()})</span>
                )}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>Created: {formatDate(editRequest.createdAt)}</span>
            </div>

            <div className="mt-2">
              <span className="font-medium">Changes:</span> {getChangeSummary()}
            </div>

            {editRequest.reason && (
              <div className="mt-1 italic">
                <span className="font-medium">Reason:</span> {editRequest.reason}
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to={`/edit-requests/${editRequest.id}`}
              className="inline-flex items-center px-3 py-1.5 border border-yellow-600 text-sm font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
            >
              <Eye className="h-4 w-4 mr-1.5" />
              View Changes
            </Link>

            {isAssignedReviewer() && (
              <Link
                to={`/edit-requests/${editRequest.id}`}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
              >
                <ArrowRight className="h-4 w-4 mr-1.5" />
                Review Now
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRequestBanner;
